// Adding a trade by hand, opened from a day on the calendar (or "Add a trade" on
// the calendar). P&L is worked out with the contract's dollar value per point, so
// a 5-point ES trade is $250, not $5. Anything else can be entered in dollars.
import { useEffect, useState } from 'react';
import { api } from './api';
import { contractFor } from './tools/contracts';
import { formatMoney, tradingDayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import AddTradeImport from './AddTradeImport';

// The tab last used, so a trader who always imports lands on Import a file.
const TAB_KEY = 'mm-add-trade-tab';
function storedTab() {
  try { return localStorage.getItem(TAB_KEY) === 'import' ? 'import' : 'manual'; } catch { return 'manual'; }
}

const num = (v) => {
  const n = parseFloat(String(v).replace(/[, $]/g, ''));
  return Number.isFinite(n) ? n : null;
};

function ManualTrade({ date, accounts, defaultAccountId, onSaved, onClose, onSessionExpired, onImportFile, startOn, pictures, picturesPreview }) {
  const { t } = useI18n();
  const [tab, setTab] = useState(onImportFile ? startOn || storedTab() : 'manual');
  const chooseTab = (next) => {
    setTab(next);
    try { localStorage.setItem(TAB_KEY, next); } catch { /* not remembered in private mode */ }
  };
  const [form, setForm] = useState({
    // 'new' makes an account with the trade (name and type below); it's the start with no accounts.
    accountId: defaultAccountId || (accounts[0] ? accounts[0]._id : 'new'),
    symbol: '', side: 'Long', size: '1', entryPrice: '', exitPrice: '', date,
    entryTime: '', exitTime: '', fees: '', pnl: '', notes: '',
  });
  const [newAccount, setNewAccount] = useState({ name: '', type: 'Evaluation' });
  // Made on an earlier try whose trade didn't save, so trying again doesn't make it twice.
  const [createdId, setCreatedId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const contract = contractFor(form.symbol);
  const entry = num(form.entryPrice);
  const exit = num(form.exitPrice);
  const size = num(form.size) || 0;
  const direction = form.side === 'Short' ? -1 : 1;
  const points = entry !== null && exit !== null ? (exit - entry) * direction : null;
  const typedPnl = num(form.pnl);
  // Dollars per point: the contract's value for known futures, otherwise 1 (stocks and other markets).
  const computed = points === null ? null : points * size * (contract ? contract.point : 1);
  const pnl = typedPnl !== null ? typedPnl : computed;
  // An entry at or after 6 PM New York time belongs to the next trading day.
  const opensOn = form.date && form.entryTime ? tradingDayKey(new Date(`${form.date}T${form.entryTime}:00`)) : form.date;
  const shifted = opensOn && opensOn !== form.date;

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (exit === null || entry === null) return setError(t('Enter an entry and an exit price.'));
    if (form.accountId === 'new' && !createdId && !newAccount.name.trim()) return setError(t('Give the new account a name.'));
    setSaving(true);
    try {
      let accountId = form.accountId;
      if (accountId === 'new') {
        accountId = createdId;
        if (!accountId) {
          const account = await api('/accounts', { method: 'POST', body: { name: newAccount.name.trim(), type: newAccount.type, platform: 'Other' } });
          accountId = account._id;
          setCreatedId(accountId);
        }
      }
      const at = (time) => new Date(`${form.date}T${time || '12:00'}:00`);
      const opened = at(form.entryTime);
      const closed = form.exitTime ? at(form.exitTime) : null;
      const body = {
        accountId: accountId || undefined,
        symbol: form.symbol.trim().toUpperCase(),
        side: form.side,
        size,
        entryPrice: entry,
        exitPrice: exit,
        pnl: Math.round(pnl * 100) / 100,
        fees: num(form.fees) || 0,
        date: opened.toISOString(),
        ...(closed && closed > opened ? { exitDate: closed.toISOString() } : {}),
        notes: form.notes.trim(),
      };
      const saved = await api('/trades', { method: 'POST', body });
      onSaved(saved);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
    return undefined;
  }

  const dayLabel = new Date(`${form.date}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal manual-trade" role="dialog" aria-modal="true" aria-labelledby="manual-trade-title" onSubmit={submit}>
        <div className="modal-header">
          <div>
            <h2 id="manual-trade-title">{t('Add a trade')}</h2>
            <p className="manual-trade-day">{dayLabel}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>

        {onImportFile && (
          <div className="add-trade-tabs" role="tablist" aria-label={t('How to add trades')}>
            {[['manual', 'Type it in'], ['import', 'Import a file']].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'active' : ''} onClick={() => chooseTab(id)}>{t(label)}</button>
            ))}
          </div>
        )}

        {tab === 'import' ? <AddTradeImport onFile={onImportFile} pictures={pictures} picturesPreview={picturesPreview} /> : (<>

        <div className="fields manual-trade-fields">
          <label>
            {t('Account')}
            <select value={form.accountId} onChange={set('accountId')}>
              {accounts.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
              <option value="new">{t('+ New account…')}</option>
            </select>
          </label>
          {form.accountId === 'new' && !createdId && (
            <div className="manual-new-account">
              <label>
                {t('New account name')}
                <input value={newAccount.name} onChange={(e) => setNewAccount((a) => ({ ...a, name: e.target.value }))} maxLength={60} placeholder={t('e.g. Apex 50K')} />
              </label>
              <label>
                {t('Type')}
                <select value={newAccount.type} onChange={(e) => setNewAccount((a) => ({ ...a, type: e.target.value }))}>
                  {['Evaluation', 'Funded', 'Live', 'Personal'].map((x) => <option key={x} value={x}>{t(x)}</option>)}
                </select>
              </label>
              <span className="field-hint">{t('Saving the trade creates this account. Add its firm’s rules later in Accounts.')}</span>
            </div>
          )}
          <label>
            {t('Symbol')}
            <input value={form.symbol} onChange={set('symbol')} placeholder="ES" required autoFocus />
          </label>
          <label>
            {t('Side')}
            <select value={form.side} onChange={set('side')}>
              <option value="Long">{t('Long')}</option>
              <option value="Short">{t('Short')}</option>
            </select>
          </label>
          <label>
            {t('Contracts')}
            <input type="number" step="any" min="0" value={form.size} onChange={set('size')} required />
          </label>
          <label>
            {t('Entry price')}
            <input type="text" inputMode="decimal" value={form.entryPrice} onChange={set('entryPrice')} placeholder="7790.25" required />
          </label>
          <label>
            {t('Exit price')}
            <input type="text" inputMode="decimal" value={form.exitPrice} onChange={set('exitPrice')} placeholder="7795.25" required />
          </label>
          <label>
            {t('Date')}
            <input type="date" value={form.date} onChange={set('date')} required />
          </label>
          <label>
            {t('Entry time (optional)')}
            <input type="time" value={form.entryTime} onChange={set('entryTime')} />
            {shifted && (
              <span className="field-hint">
                {t('This trade will show on {day}: futures trading days start at the 6 PM New York reopen.', {
                  day: new Date(`${opensOn}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'short', day: 'numeric' }),
                })}
              </span>
            )}
          </label>
          <label>
            {t('Exit time (optional)')}
            <input type="time" value={form.exitTime} onChange={set('exitTime')} />
          </label>
          <label>
            {t('Fees (optional)')}
            <input type="text" inputMode="decimal" value={form.fees} onChange={set('fees')} placeholder="2.80" />
          </label>
          <label>
            {t('P&L in dollars (optional)')}
            <input type="text" inputMode="decimal" value={form.pnl} onChange={set('pnl')} placeholder={computed !== null ? computed.toFixed(2) : ''} />
          </label>
        </div>

        <p className="manual-trade-preview">
          {pnl === null
            ? t('Enter your prices to see the P&L.')
            : typedPnl !== null
              ? t('P&L: {amount} (the amount you entered).', { amount: formatMoney(pnl) })
              : contract
                ? t('P&L: {amount} ({symbol}: {value} per point).', { amount: formatMoney(pnl), symbol: contract.symbol, value: usd.format(contract.point) })
                : t('P&L: {amount} (price difference × size). Not a futures contract we know, so enter the P&L in dollars if this isn’t right.', { amount: formatMoney(pnl) })}
          {num(form.fees) ? ` ${t('Fees {amount} are subtracted from it.', { amount: usd.format(num(form.fees)) })}` : ''}
        </p>

        <label>
          {t('Notes')}
          <textarea value={form.notes} onChange={set('notes')} rows="3" placeholder={t('Why did you take this trade? What did you learn?')} />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Add trade')}</button>
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
        </div>
        </>)}
      </form>
    </div>
  );
}

export default ManualTrade;
